<template>
    <SkeletonTable
        v-if="loader"
        :rows="loaderOptions.rows"
        :columns="loaderOptions.columns"
    />
    <DataTable
        v-else
        :fields="fields"
        :items="items"
        :sort-key="primaryKey"
        :sort-compare="sortCompare"
        :page-size="25"
        :scrollable-table="tableIsScrollable"
        :last-col-sticky="lastColSticky"
        :tbody-tr-class="tbodyTrClass"
        :sortable="sortable"
        :external-sorting="externalSorting"
        @row-clicked="$emit('row-clicked', $event)"
        @sort-changed="$emit('sort-changed', $event)"
    >
        <template
            v-if="tableInfoText"
            #top-row
        >
            <td
                :colspan="infoRowSize"
                class="text-center bg-green-bright"
            >
                <div class="table-info-container">
                    <div
                        class="d-md-flex table-info-text"
                        :style="`margin-left: ${scrollX}px`"
                    >
                        <div>
                            {{ tableInfoText }}
                        </div>
                        <a
                            href="#"
                            class="cursor-pointer ml-2 font-weight-500"
                            @click="() => $emit('table-info-clicked')"
                        >
                            {{ tableInfoLinkText }}
                        </a>
                    </div>
                </div>
            </td>
        </template>
        <template #filter-slot>
            <slot name="filter-slot"></slot>
        </template>

        <template
            v-for="(_, name) in $slots"
            #[name]="slotData"
        >
            <slot
                v-bind="slotData"
                :name="name"
            />
        </template>
    </DataTable>
</template>

<script>
import DataTable from './DataTable.vue';
import SkeletonTable from './SkeletonTable.vue';

export default {
    name: 'BaseTable',

    compatConfig: {
        MODE: 3,
    },

    components: {
        SkeletonTable,
        DataTable,
    },

    props: {
        tableInfoLinkText: {
            type: String,
            default: '',
        },
        tableInfoText: {
            type: String,
            default: '',
        },
        loader: Boolean,
        loaderOptions: {
            type: Object,
            default: () => ({
                rows: 3,
                columns: 4,
            }),
        },
        primaryKey: {
            type: String,
            default: 'id',
        },
        tableClass: {
            type: String,
            default: '',
        },
        singleRow: Boolean,
        alignTop: Boolean,
        stacked: {
            type: String,
            default: 'md',
        },
        cursorPointer: Boolean,
        stickyHeader: {
            type: [Boolean, String],
            default: false,
        },
        noBorderCollapse: {
            type: [Boolean],
            default: true,
        },
        scrollable: {
            type: Boolean,
            default: false,
        },
        lastColSticky: {
            type: Boolean,
            default: false,
        },
        tbodyTrClass: {
            type: [Function, String],
            default: undefined,
        },
        sortCompare: {
            type: Function,
            default: null,
        },
        sortable: {
            type: Boolean,
            default: true,
        },
        externalSorting: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['table-info-clicked', 'row-clicked', 'sort-changed'],

    data() {
        return {
            scrollX: 0,
        };
    },

    computed: {
        fields() {
            return this.$attrs.fields;
        },
        items() {
            return this.$attrs.items;
        },
        infoRowSize() {
            return this.$attrs?.fields?.length;
        },
        tableIsScrollable() {
            return this.scrollable; // Todo should also maybe depend on param or route or something?
        },
    },

    mounted() {
        if (this.tableInfoText) {
            this.$refs['base-table'].$el.addEventListener('scroll', this.updateScroll);
        }
    },

    beforeUnmount() {
        if (this.tableInfoText) {
            this.$refs['base-table'].$el.removeEventListener('scroll', this.updateScroll);
        }
    },

    methods: {
        updateScroll(event) {
            this.scrollX = event.target.scrollLeft;
        },
    },
};
</script>

<style lang="scss" scoped>
.table-info-container {
    height: 2.5rem !important;

    @include media-breakpoint-up(md) {
        height: 1.5rem !important;
    }
}

.table-info-text {
    @include media-breakpoint-down(md) {
        margin-left: 0 !important;
    }
}
</style>
